import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { authState, Device } from '@/recoil/auth';
import { useEffect, useState } from 'react';
import { notificationState } from '@/recoil/notification';
import { absentModalState, pharmacyState } from '@/recoil/pharmacy';
import useAuthQuery from '@/hooks/auth/useAuthQuery';
import Firebase, { MessagePayload } from '@/lib/firebase';
import { getPageName, reportLog } from '@/lib/logger';
import { LOG_EVENT } from '@/constants/logs';
import { useLocation } from 'react-router';
import { useQueryClient } from 'react-query';

export const FirebaseMessageActions = {
  NEW_PRESCRIPTION: 'NEW_PRESCRIPTION',
  PAID_PRESCRIPTION: 'PAID_PRESCRIPTION',
  CANCEL_PRESCRIPTION: 'CANCEL_PRESCRIPTION',
  PHARMACIST_IS_ABSENT: 'PHARMACIST_IS_ABSENT',
  PHARMACY_DELIVERY_MATCH_FAILED: 'PHARMACY_DELIVERY_MATCH_FAILED',
  PRESCRIPTION_REVISION: 'PRESCRIPTION_REVISION',
  PICKUP_DELAYED_IMMEDIATE_PAYMENT_ORDER_TO_PHARMACIST:
    'PICKUP_DELAYED_IMMEDIATE_PAYMENT_ORDER_TO_PHARMACIST',
  NEW_PRESCRIPTION_BY_IMMEDIATE_PAYMENT: 'NEW_PRESCRIPTION_BY_IMMEDIATE_PAYMENT'
} as const;

export type FirebaseMessageActions =
  typeof FirebaseMessageActions[keyof typeof FirebaseMessageActions];

const useFirebaseListener = () => {
  const auth = useRecoilValue(authState);
  const setAbsentModal = useSetRecoilState(absentModalState);
  const [backgroundNoti, setBackgroundNoti] = useState(1);
  const [notification, setNotification] = useRecoilState(notificationState);
  const location = useLocation<string>();
  const pharmacy = useRecoilValue(pharmacyState);
  const isLoggedIn = pharmacy.id !== undefined;
  const { devicesUpdateMutation } = useAuthQuery();
  const queryClient = useQueryClient();

  const checkShouldRegister = (newToken: string) => {
    const noToken = auth.devices?.length === 0;
    const freshToken = auth.devices?.every(
      (device: Device) => device.firebaseToken !== newToken
    );

    return noToken || freshToken;
  };

  const handleInit = async () => {
    Firebase.initApp();
    await Firebase.initMessaging();
    const firebaseToken = Firebase.token;
    if (!firebaseToken) return;

    const shouldRegister = checkShouldRegister(firebaseToken);
    if (shouldRegister) devicesUpdateMutation.mutate(firebaseToken);
  };

  // Foreground message
  const handleOnMessage = () => {
    Firebase.addListener(payload => {
      switchMessage(payload);

      reportLog(`${LOG_EVENT.WEB_PUSH_IMPRESSION}`, {
        event_name: LOG_EVENT.WEB_PUSH_IMPRESSION,
        page_name: getPageName(location.pathname),
        payload: {
          originalReceiveMessage: {
            notification: payload.notification,
            data: payload.data,
            to: auth.token
          }
        }
      });
    });
  };

  // Background message
  navigator.serviceWorker.onmessage = event => {
    const isImmediatelyNotice =
      event.data &&
      (event.data.type === 'NEW_NOTIFICATION' ||
        event.data.type === 'PRESCRIPTION_REVISION');
    setBackgroundNoti(backgroundNoti + 1);
    if (isImmediatelyNotice) {
      const { payload } = event.data.backgroundMessage;
      switchMessage(payload);
    }
  };

  // Switch Received Message
  const switchMessage = (payload: MessagePayload) => {
    if (!payload.data) return;

    switch (payload.data.action) {
      case FirebaseMessageActions.NEW_PRESCRIPTION:
        return setNotification({
          ...notification,
          recent: {
            action: FirebaseMessageActions.NEW_PRESCRIPTION,
            orderId: payload.data.orderId,
            ...payload.notification
          }
        });
      case FirebaseMessageActions.PAID_PRESCRIPTION:
        return setNotification({
          ...notification,
          recent: {
            action: FirebaseMessageActions.PAID_PRESCRIPTION,
            ...payload.notification
          }
        });
      case FirebaseMessageActions.CANCEL_PRESCRIPTION:
        return setNotification({
          ...notification,
          recent: {
            action: FirebaseMessageActions.CANCEL_PRESCRIPTION,
            ...payload.notification
          }
        });
      case FirebaseMessageActions.PHARMACIST_IS_ABSENT:
        setAbsentModal(true);
        return setNotification({
          ...notification,
          recent: {
            action: FirebaseMessageActions.PHARMACIST_IS_ABSENT,
            ...payload.notification
          }
        });
      case FirebaseMessageActions.PHARMACY_DELIVERY_MATCH_FAILED: {
        queryClient?.invalidateQueries(['order', payload.data.orderNumber]);
        return setNotification({
          ...notification,
          recent: {
            action: FirebaseMessageActions.PHARMACY_DELIVERY_MATCH_FAILED,
            orderId: payload.data.orderNumber,
            ...payload.notification
          }
        });
      }
      case FirebaseMessageActions.PRESCRIPTION_REVISION: {
        return setNotification({
          ...notification,
          recent: {
            action: FirebaseMessageActions.PRESCRIPTION_REVISION,
            ...payload.notification,
            ...payload.data
          }
        });
      }
      case FirebaseMessageActions.PICKUP_DELAYED_IMMEDIATE_PAYMENT_ORDER_TO_PHARMACIST: {
        return setNotification({
          ...notification,
          recent: {
            action:
              FirebaseMessageActions.PICKUP_DELAYED_IMMEDIATE_PAYMENT_ORDER_TO_PHARMACIST,
            orderId: payload.data.orderId,
            ...payload.notification
          }
        });
      }
      case FirebaseMessageActions.NEW_PRESCRIPTION_BY_IMMEDIATE_PAYMENT: {
        return setNotification({
          ...notification,
          recent: {
            action:
              FirebaseMessageActions.NEW_PRESCRIPTION_BY_IMMEDIATE_PAYMENT,
            orderId: payload.data.orderId,
            ...payload.notification
          }
        });
      }
    }
  };

  /**
   * Notification check logic
   * https://developer.mozilla.org/ko/docs/Web/API/notification#example
   */
  useEffect(() => {
    if (!isLoggedIn) return;
    if (!('Notification' in window)) return;
    if (Notification.permission === 'denied') return;
    // todo Notification Permission 거부 시 에러 처리 해야함
    handleInit();
    handleOnMessage();
  }, [isLoggedIn]);
};

export default useFirebaseListener;
